import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import {ThemeProvider} from '@mui/material'
import theme from './theme'; // Import the theme from the other file
import {createBrowserRouter,RouterProvider, useRouteError} from "react-router-dom";
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';

// Shown when a lazy chunk fails to load (e.g. ChunkLoadError); offers retry
function RouteErrorFallback() {
  const error = useRouteError();
  const isChunkError = error?.name === 'ChunkLoadError' || (error?.message && error.message.includes('Loading chunk'));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 3, backgroundColor: '#f5f5f5' }}>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
        {isChunkError ? 'Failed to load this page.' : 'Something went wrong.'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center', maxWidth: 400 }}>
        {isChunkError ? 'Try refreshing the page. If the dev server restarted, a refresh usually fixes it.' : (error?.message || String(error))}
      </Typography>
      <Button variant="contained" onClick={() => window.location.reload()} sx={{ textTransform: 'none' }}>
        Retry
      </Button>
    </Box>
  );
}

// Lazy load all route components for code splitting
const Singleservicespage = lazy(() => import('./pages/singleservicespage'));
const Contactpage = lazy(() => import('./pages/contactpage'));
const Aboutpage = lazy(() => import('./pages/aboutpage'));
const Servicespage = lazy(() => import('./pages/servicespage'));
const Yeastarpage = lazy(() => import('./pages/yeastarpage'));
const Yealinkpage = lazy(() => import('./pages/yealinkpage'));
const Blog = lazy(() => import('./pages/blog'));
const BlogDetailPage = lazy(() => import('./pages/blogdetailpage'));
const DetailPageYealinkWrapper = lazy(() => import('./components/DetailPageYealinkWrapper'));
const DetailPageYeastarWrapper = lazy(() => import('./components/DetailPageYeastarWrapper'));
const YeastarTAFXSGatewaysPage = lazy(() => import('./pages/YeastarTAFXSGatewaysPage'));
const YeastarTAFXOGatewaysPage = lazy(() => import('./pages/YeastarTAFXOGatewaysPage'));
const YeastarTEPRIGatewaysPage = lazy(() => import('./pages/YeastarTEPRIGatewaysPage'));
const YeastarATAAdaptersPage = lazy(() => import('./pages/YeastarATAAdaptersPage'));
const YealinkTeamsZoomPage = lazy(() => import('./pages/YealinkTeamsZoomPage'));
const SEOPage = lazy(() => import('./pages/SEOPage'));

// Loading fallback component
const LoadingFallback = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      backgroundColor: '#FFFFFF'
    }}
  >
    <CircularProgress sx={{ mb: 2 }} />
    <Typography variant="body2" color="text.secondary">Loading...</Typography>
  </Box>
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
  },
  {
    path: "/Contactpage",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Contactpage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/Aboutpage",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Aboutpage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/Servicespage",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Servicespage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/Singleservicespage",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Singleservicespage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/blog",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Blog/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/blog/:slug",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <BlogDetailPage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/yeastar-products",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Yeastarpage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/yealink-products",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Yealinkpage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/yealink-products/microsoft-teams-zoom-devices",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <YealinkTeamsZoomPage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/yealink-products/:permalink",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <DetailPageYealinkWrapper/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/yeastar-products/:permalink",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <DetailPageYeastarWrapper/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/yeastar-products/yeastar-ta-fxs-voip-gateways-pakistan",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <YeastarTAFXSGatewaysPage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/yeastar-products/yeastar-ta-fxo-voip-gateways-pakistan",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <YeastarTAFXOGatewaysPage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/yeastar-products/yeastar-te-e1-t1-pri-voip-gateways-pakistan",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <YeastarTEPRIGatewaysPage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/yeastar-products/yeastar-analog-telephone-adapters-pakistan",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <YeastarATAAdaptersPage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/seo-services",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <SEOPage/>
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
]);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <HelmetProvider>
    <ThemeProvider theme={theme}>
      <RouterProvider router={router} />
    </ThemeProvider>
  </HelmetProvider>,
);

